import * as React from 'react';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import './ToastPopup.scss';

export type ToastType = 'success' | 'warning' | 'error';

export interface ToastPopupProps {
  header: string;
  body: React.ReactNode;
  type: ToastType;
  duration?: number;
  onClose?: () => void;
}

export interface ToastPopupState {
  visible: boolean;
}

const icons: Record<ToastType, IconDefinition> = {
  success: faCheckCircle,
  warning: faExclamationTriangle,
  error: faExclamationCircle
};

const defaultDuration = 5000;

class ToastPopup extends React.Component<ToastPopupProps, ToastPopupState> {
  private timer?: number;

  constructor(props: ToastPopupProps) {
    super(props);
    this.state = {
      visible: true
    };

    this.dismiss = this.dismiss.bind(this);
  }

  componentDidMount() {
    this.setupTimer();
  }

  componentDidUpdate(prevProps: ToastPopupProps) {
    if (
      prevProps.header !== this.props.header ||
      prevProps.body !== this.props.body ||
      prevProps.type !== this.props.type ||
      prevProps.duration !== this.props.duration
    ) {
      this.setState({ visible: true }, () => {
        this.setupTimer();
      });
    }
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  private dismiss() {
    if (!this.state.visible) {
      return;
    }

    this.setState({ visible: false });
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  private setupTimer() {
    this.clearTimer();

    const duration = this.props.duration !== undefined ? this.props.duration : defaultDuration;
    if (duration === 0) {
      return;
    }

    this.timer = window.setTimeout(() => {
      this.dismiss();
    }, duration);
  }

  private clearTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  render() {
    if (!this.state.visible) {
      return null;
    }

    const duration = this.props.duration !== undefined ? this.props.duration : defaultDuration;

    return (
      <div
        className={`toast-popup toast-popup--${this.props.type}`}
        role="status"
        aria-live="polite"
        data-duration={duration}
      >
        <div className="toast-popup__content">
          <div className="toast-popup__header">
            <span className="toast-popup__icon" aria-hidden="true">
              <FontAwesomeIcon icon={icons[this.props.type]} />
            </span>
            <span className="toast-popup__header-text">{this.props.header}</span>
            <button
              type="button"
              className="toast-popup__close"
              onClick={this.dismiss}
              aria-label="Close toast"
            >
              &times;
            </button>
          </div>
          <div className="toast-popup__body">{this.props.body}</div>
        </div>
      </div>
    );
  }
}

export default ToastPopup;
